import { Trans } from '@lingui/react/macro';
import { Chip, Typography } from '@mui/material';

import type { ImageKeys, ImageObject } from '@/modules/annotator/types';
import type { Visit } from '@/modules/referrals/components/VisitAttachments/visitAttachmentsTypes';
import type { ImageManifestForRbl } from '@/react-query/usePatientRblData';
import { getSelectedImagePath } from '@/utils/cornerstone/useCornerstone';
import { getVersionAndTypeBeingDisplayed } from '@/utils/file';

import type { DevToolInfo } from '../types/types';

// Moved constants from constants.ts
export const EnvToBucket = {
  production: 'videa-images-prod',
  development: 'videa-images-dev',
  staging: 'videa-images-stg',
};

export const IMAGE_MANIFEST_RBL_TITLE =
  'Image Manifests from this date not Displayed In UI';

export const AI_INFORMATION_TITLE = 'AI Information';
export const PATIENT_INFORMATION_TITLE = 'Patient Information';

export const getS3Path = (input: {
  imageKeys?: ImageKeys;
  originalPath?: string;
}) => {
  const s3Path = input.imageKeys?.s3path;

  if (s3Path) {
    return s3Path;
  }

  if (input.originalPath) {
    const match = input.originalPath.match(/uploads\/.*?\.png/);
    const matchedStr = match ? match[0] : null;

    if (!matchedStr) {
      return null;
    }

    const bucket =
      EnvToBucket[process.env.NODE_ENV! as keyof typeof EnvToBucket] ??
      EnvToBucket.development;

    return `s3://${bucket}/${matchedStr}`;
  }
  return null;
};

export const getEstimatedVisitDate = (
  visitId: number,
  patientVisitsData?: Visit[],
) => {
  if (visitId && patientVisitsData) {
    const currentVisit = patientVisitsData.find(
      (visit) => visit.id === visitId,
    );
    return currentVisit?.visitDate;
  }
};

export const formatInformationToDisplay = (
  activeImage: ImageObject,
  user: any,
  patient: any,
  estimatedVisitDate: Date | null,
  isHQEnabled: boolean,
  preferDisplayOverSharpened: boolean,
  imageManifests: ImageManifestForRbl[] | undefined,
) => {
  const pathToDisplay = getSelectedImagePath(
    isHQEnabled,
    activeImage,
    preferDisplayOverSharpened,
  );

  const pathUrl = pathToDisplay?.startsWith('https://')
    ? new URL(pathToDisplay as string)
    : null;
  const fileName = pathUrl?.pathname.split('/').at(-1);

  const pathInfo = fileName ? getVersionAndTypeBeingDisplayed(fileName) : null;

  const imageInformation: DevToolInfo = {
    title: 'Image Information',
    data: [],
  };

  const imageManifestInformation: DevToolInfo = {
    title: `${imageManifests?.length} ${IMAGE_MANIFEST_RBL_TITLE}`,
    data: [],
  };

  const patientInformation: DevToolInfo = {
    title: PATIENT_INFORMATION_TITLE,
    data: [],
  };

  const aiInformation: DevToolInfo = {
    title: AI_INFORMATION_TITLE,
    data: [],
  };

  if (patient) {
    patientInformation.data.push({
      name: 'Imaging Patient ID',
      value: patient.id ?? patient.patientId,
    });
    patientInformation.data.push({
      name: 'Estimated visit date',
      value: `${patient.estimatedVisitDate ?? estimatedVisitDate} (UTC)`,
    });
  }

  if (user) {
    patientInformation.data.push({
      name: 'Organization id',
      value: user.organizations?.[0]?.organizationId,
    });
    patientInformation.data.push({
      name: 'Practice id',
      value: user.primaryPracticeId,
    });
  }

  if (activeImage) {
    const s3Path = getS3Path({
      imageKeys: activeImage.imageKeys,
      originalPath: activeImage.originalPath,
    });
    const s3PathSplit = activeImage.imageKeys?.s3path?.split('/') || [];
    aiInformation.data.push({
      name: 'AI findings for image',
      value: JSON.stringify(activeImage.models),
      copyable: true,
      shorten: true,
    });
    imageInformation.data.push({
      name: 'View classifier finding',
      value: activeImage.detectedImageType ?? 'Not Bitewing',
    });
    imageInformation.data.push({
      name: 'File name',
      value: s3PathSplit[s3PathSplit.length - 1],
    });

    // Add all three paths
    if (activeImage.sharpenedPath) {
      imageInformation.data.push({
        name: 'Sharpened Path (HQ image)',
        value: activeImage.sharpenedPath,
        copyable: true,
        shorten: true,
      });
    }

    if (activeImage.path) {
      imageInformation.data.push({
        name: 'Path (Non-HQ image)',
        value: activeImage.path,
        copyable: true,
        shorten: true,
      });
    }

    if (activeImage.originalPath) {
      imageInformation.data.push({
        name: 'Original Path (Original image)',
        value: activeImage.originalPath,
        copyable: true,
        shorten: true,
      });
    }

    s3Path &&
      imageInformation.data.push({
        name: 'S3 path (deid)',
        value: s3Path,
        shorten: true,
        copyable: true,
      });
    imageInformation.data.push({
      name: 'Image display details',
      value: pathInfo ? JSON.stringify({ ...pathInfo, isHQEnabled }) : '',
      shorten: false,
      copyable: true,
      display: (
        <>
          <Typography>
            <Trans>File Name: {pathInfo?.fileName}</Trans>
          </Typography>
          <Chip label={pathInfo?.version} color="secondary" size="small" />
          <Chip label={pathInfo?.ext} color="secondary" size="small" />
          <Chip
            label={isHQEnabled ? 'HQ On' : 'HQ off'}
            color="secondary"
            size="small"
          />
        </>
      ),
    });
  }

  if (!activeImage && imageManifests && imageManifests.length > 0) {
    imageManifests.forEach((manifest) => {
      imageManifestInformation.data.push({
        name: `ImageManifestId: ${manifest.imageManifestId}`,
        value: JSON.stringify(manifest, null, 2),
      });
    });
  }

  return [
    patientInformation,
    aiInformation,
    imageInformation,
    imageManifestInformation,
  ];
};
